import Figure from "@hydrologiq/shared/figures/generic/Figure"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import { AssetHeadlineFigure } from "@hydrologiq/gql-experience/graphql"
import { isCompactFigure } from "@hydrologiq/shared/figures/generic/figureVariant"

interface HoursToFillFigureProps {
  data?: AssetHeadlineFigure
  loading?: boolean
  error?: string
  isPlaceholder?: boolean
  compact?: boolean
}

/**
 * Format a duration in minutes as "Xh Ym" (product decision: X hours Y minutes,
 * or HH:MM — either acceptable). Returns null for non-finite / negative input.
 *
 * Sub-minute values render as "<1m" rather than "0h 0m", so a nearly-full store can't be
 * mistaken for a full one (or for a missing reading).
 */
export function formatHoursMinutes(totalMinutes: number | null | undefined): string | null {
  if (totalMinutes == null || !Number.isFinite(totalMinutes) || totalMinutes < 0) return null
  if (totalMinutes > 0 && totalMinutes < 1) return "<1m"
  const hours = Math.floor(totalMinutes / 60)
  const minutes = Math.round(totalMinutes % 60)
  return `${hours}h ${minutes}m`
}

/**
 * Burnaston overview card — "Time to Fill Storage".
 *
 * Logic (from ticket): (total max capacity − total current weight) ÷ current total
 * production flow. Computed in core and delivered via installationHeadlines.
 *
 * Resolved: "total max capacity" / "total current weight" = the INSTALLATION weight
 * (Adam — not per-asset). Units reconcile via KG_PER_NL (see ./hydrogenUnits.ts):
 * capacity/weight are kg and the NL/h flow converts to kg/h before dividing.
 *
 * Core distinguishes three non-normal states (see `_compute_hours_to_fill` in
 * installation_headline_service.py); this card surfaces them clearly:
 *
 *   fill rate > 0             → show estimated time to fill (normal)
 *   available: true, value 0  → storage is already at capacity ("Full")
 *   neverFills, level > 90%   → "Storage full" (no production, but nearly/at capacity)
 *   neverFills, level ≤ 90%   → "Not started" (no production, storage not full)
 *   available: false, no metadata → missing level / capacity / flow reading
 *
 * Unit confirmed as minutes (core sets `unit: "minutes"`).
 */
function HoursToFillFigure({
  data,
  loading,
  error,
  isPlaceholder,
  compact = isCompactFigure("summary"),
}: HoursToFillFigureProps) {
  const formatted = formatHoursMinutes(data?.value ?? null)
  // Core reports "never fills" as unavailable, so without this the card would claim a data
  // gap when in fact we know exactly what's happening: production has stopped.
  const meta = data?.metadata as { neverFills?: boolean; storagePercent?: number | null } | null
  const neverFills = meta?.neverFills === true
  const storagePercent = meta?.storagePercent ?? null
  const isFull = data?.available === true && data.value === 0

  // When not filling, differentiate: storage nearly full (>90%) vs not started.
  const storageNearlyFull = neverFills && storagePercent != null && storagePercent > 90

  const { subText, value } = storageNearlyFull || isFull
    ? { subText: "Storage at capacity", value: "Full" }
    : neverFills
    ? { subText: "No production", value: "Not started" }
    : { subText: "Estimated", value: formatted ?? "—" }

  return (
    <Figure
      // Display copy is owned here, not taken from `data.label`: core still sends the old
      // "Hours to Fill Storage" wording, and the sibling Burnaston cards hardcode their headings too.
      heading="Time to Fill Storage"
      loading={loading}
      error={error}
      // `neverFills` is a known state, not an absent one — override the caller's placeholder
      // flag (derived from `available`) so the card shows the reason instead of "Waiting for
      // more data". A genuinely missing reading still falls through to the placeholder.
      isPlaceholder={neverFills ? false : isPlaceholder ?? !data?.available}
      compact={compact}
    >
      <FigureContent.Root>
        <FigureContent.SubText>{subText}</FigureContent.SubText>
        <FigureContent.Value>{value}</FigureContent.Value>
      </FigureContent.Root>
    </Figure>
  )
}

export default HoursToFillFigure
